import {EcgStatement} from "./types"

/**
 * Statements the backend catalogue has no key for.
 *
 * WHY THIS FILE EXISTS
 *
 * `catalogue.generated.ts` is the vocabulary the *algorithm* can emit: it knows
 * "Atrial flutter", but not "with 2:1 conduction"; it has nine intraventricular
 * conduction entries but no rSR' and no conduction delay. A technician
 * reporting a 12-lead resting ECG needs the reporting vocabulary, which is
 * wider than the algorithm's output.
 *
 * PROVENANCE — READ THIS BEFORE TRUSTING THE WORDING
 *
 * Every string in `catalogue.generated.ts` is the backend's own text, character
 * for character, and is therefore identical to what the PDF report prints.
 * Nothing in THIS file is. These statements were written for this screen
 * because no source text exists for them, in any language. They are marked
 * `origin: "local"` when the catalogue is assembled, so the difference is
 * visible in the data and not only in this comment.
 *
 * The Italian and Ukrainian here follow the conventions of the backend
 * catalogue — English takes no closing full stop, Italian always does,
 * Ukrainian does not; the `%s` lead slot sits where the leads are named — but
 * they have not been reviewed by a native-speaking clinician. If a statement
 * below is ever added to `restEcgReport*.properties`, delete it here: the
 * generated file wins, because that is the text the report prints.
 *
 * SHORTCUTS
 *
 * The backend statements need none — their key tails already are the codes
 * (`LAFB`, `RBBB`, `SVEB`), so they match for free. These keys are descriptive
 * rather than code-like (`aFlutter2to1`), so the abbreviations a technician
 * actually types are listed explicitly. `normalise()` keeps punctuation, so
 * "2:1" is typeable exactly as it is written in a report.
 */
export const CUSTOM_STATEMENTS: EcgStatement[] = [
    // ---- rhythm ----
    {
        key: "heartRhythm.sinusArrhythmia",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["sinarr"],
        text: {
            en: "Sinus arrhythmia",
            it: "Aritmia sinusale.",
            uk: "Синусова аритмія",
        },
    },
    {
        key: "heartRhythm.ectopicAtrialRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["ear"],
        text: {
            en: "Ectopic atrial rhythm",
            it: "Ritmo atriale ectopico.",
            uk: "Ектопічний передсердний ритм",
        },
    },
    {
        key: "heartRhythm.atrialTachycardia",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["at"],
        text: {
            en: "Atrial tachycardia",
            it: "Tachicardia atriale.",
            uk: "Передсердна тахікардія",
        },
    },
    {
        key: "heartRhythm.multifocalAtrialTachycardia",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["mat"],
        text: {
            en: "Multifocal atrial tachycardia",
            it: "Tachicardia atriale multifocale.",
            uk: "Багатовогнищева передсердна тахікардія",
        },
    },
    {
        // both this and the 2:1 AV block answer "2:1", and both are offered:
        // which one is meant depends on the atrial rate, which is the
        // technician's call and not something a suggestion list should guess
        key: "heartRhythm.aFlutter2to1",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["afl2", "afl2:1", "2:1"],
        text: {
            en: "Atrial flutter with 2:1 atrioventricular conduction",
            it: "Flutter atriale con conduzione atrioventricolare 2:1.",
            uk: "Тріпотіння передсердь з атріовентрикулярним проведенням 2:1",
        },
    },
    {
        key: "heartRhythm.aFlutterVariable",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["aflvar"],
        text: {
            en: "Atrial flutter with variable atrioventricular conduction",
            it: "Flutter atriale con conduzione atrioventricolare variabile.",
            uk: "Тріпотіння передсердь зі змінним атріовентрикулярним проведенням",
        },
    },
    {
        key: "heartRhythm.fibrilloFlutter",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["fibfl", "fibrilloflutter"],
        text: {
            en: "Atrial fibrillation-flutter",
            it: "Fibrillo-flutter atriale.",
            uk: "Фібриляція-тріпотіння передсердь",
        },
    },
    {
        key: "heartRhythm.aFibrillationRapidResponse",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["rvr", "afrvr"],
        text: {
            en: "Atrial fibrillation with rapid ventricular response",
            it: "Fibrillazione atriale ad elevata risposta ventricolare.",
            uk: "Фібриляція передсердь із швидкою шлуночковою відповіддю",
        },
    },
    {
        key: "heartRhythm.aFibrillationSlowResponse",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["svr", "afsvr"],
        text: {
            en: "Atrial fibrillation with slow ventricular response",
            it: "Fibrillazione atriale a bassa risposta ventricolare.",
            uk: "Фібриляція передсердь із повільною шлуночковою відповіддю",
        },
    },
    {
        key: "heartRhythm.acceleratedJunctionalRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["ajr"],
        text: {
            en: "Accelerated junctional rhythm",
            it: "Ritmo giunzionale accelerato.",
            uk: "Прискорений вузловий ритм",
        },
    },
    {
        key: "heartRhythm.acceleratedIdioventricularRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["aivr"],
        text: {
            en: "Accelerated idioventricular rhythm",
            it: "Ritmo idioventricolare accelerato.",
            uk: "Прискорений ідіовентрикулярний ритм",
        },
    },
    {
        key: "heartRhythm.nonSustainedVTachy",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["nsvt"],
        text: {
            en: "Non-sustained ventricular tachycardia",
            it: "Tachicardia ventricolare non sostenuta.",
            uk: "Нестійка шлуночкова тахікардія",
        },
    },
    {
        key: "heartRhythm.sinoatrialBlock",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["sab"],
        text: {
            en: "Sinoatrial block",
            it: "Blocco senoatriale.",
            uk: "Синоатріальна блокада",
        },
    },
    {
        key: "heartRhythm.sinusArrest",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["sinarrest"],
        text: {
            en: "Sinus arrest",
            it: "Arresto sinusale.",
            uk: "Зупинка синусового вузла",
        },
    },
    {
        key: "heartRhythm.atrialPacedRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["apace"],
        text: {
            en: "Atrial paced rhythm",
            it: "Ritmo da pacemaker atriale.",
            uk: "Ритм передсердної електрокардіостимуляції",
        },
    },
    {
        key: "heartRhythm.ventricularPacedRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["vpace"],
        text: {
            en: "Ventricular paced rhythm",
            it: "Ritmo da pacemaker ventricolare.",
            uk: "Ритм шлуночкової електрокардіостимуляції",
        },
    },
    {
        key: "heartRhythm.dualChamberPacedRhythm",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["ddd", "dualpace"],
        text: {
            en: "Dual-chamber paced rhythm",
            it: "Ritmo da pacemaker bicamerale.",
            uk: "Ритм двокамерної електрокардіостимуляції",
        },
    },
    {
        key: "heartRhythm.blockedAtrialEctopic",
        section: "heartRhythm",
        leads: false,
        shortcuts: ["bpac"],
        text: {
            en: "Blocked atrial ectopic beat",
            it: "Battito ectopico atriale bloccato.",
            uk: "Блокований передсердний ектопічний комплекс",
        },
    },

    // ---- atrioventricular conduction ----
    {
        key: "avConduction.mobitzTypeI",
        section: "avConduction",
        leads: false,
        shortcuts: ["mobitz1", "wenckebach"],
        text: {
            en: "Second-degree atrioventricular block, Mobitz type I (Wenckebach)",
            it: "Blocco atrioventricolare di secondo grado, Mobitz tipo I (Wenckebach).",
            uk: "Атріовентрикулярна блокада другого ступеня, Мобітц I (Венкебаха)",
        },
    },
    {
        key: "avConduction.mobitzTypeII",
        section: "avConduction",
        leads: false,
        shortcuts: ["mobitz2"],
        text: {
            en: "Second-degree atrioventricular block, Mobitz type II",
            it: "Blocco atrioventricolare di secondo grado, Mobitz tipo II.",
            uk: "Атріовентрикулярна блокада другого ступеня, Мобітц II",
        },
    },
    {
        key: "avConduction.twoToOneAVBlock",
        section: "avConduction",
        leads: false,
        shortcuts: ["avb2:1", "avb21", "2:1"],
        text: {
            en: "2:1 atrioventricular block",
            it: "Blocco atrioventricolare 2:1.",
            uk: "Атріовентрикулярна блокада 2:1",
        },
    },
    {
        key: "avConduction.highGradeAVBlock",
        section: "avConduction",
        leads: false,
        shortcuts: ["hgavb"],
        text: {
            en: "High-grade atrioventricular block",
            it: "Blocco atrioventricolare avanzato.",
            uk: "Атріовентрикулярна блокада високого ступеня",
        },
    },
    {
        key: "avConduction.avDissociation",
        section: "avConduction",
        leads: false,
        shortcuts: ["avd"],
        text: {
            en: "Atrioventricular dissociation",
            it: "Dissociazione atrioventricolare.",
            uk: "Атріовентрикулярна дисоціація",
        },
    },

    // ---- atrial enlargement ----
    {
        key: "atrialHypertrophy.biatrialEnlargement",
        section: "atrialHypertrophy",
        leads: false,
        shortcuts: ["bae"],
        text: {
            en: "Biatrial enlargement",
            it: "Ingrandimento biatriale.",
            uk: "Збільшення обох передсердь",
        },
    },

    // ---- ventricular hypertrophy ----
    {
        key: "hypertrophy.leftVentricularVoltage",
        section: "hypertrophy",
        leads: false,
        shortcuts: ["lvh"],
        text: {
            en: "Left ventricular hypertrophy by voltage criteria",
            it: "Ipertrofia ventricolare sinistra secondo i criteri di voltaggio.",
            uk: "Гіпертрофія лівого шлуночка за вольтажними критеріями",
        },
    },
    {
        key: "hypertrophy.leftVentricularWithRepolarisation",
        section: "hypertrophy",
        leads: false,
        shortcuts: ["lvhstrain", "strain"],
        text: {
            en: "Left ventricular hypertrophy with repolarisation abnormality",
            it: "Ipertrofia ventricolare sinistra con alterazioni della ripolarizzazione.",
            uk: "Гіпертрофія лівого шлуночка з порушенням реполяризації",
        },
    },
    {
        key: "hypertrophy.rightVentricular",
        section: "hypertrophy",
        leads: false,
        shortcuts: ["rvh"],
        text: {
            en: "Right ventricular hypertrophy",
            it: "Ipertrofia ventricolare destra.",
            uk: "Гіпертрофія правого шлуночка",
        },
    },

    // ---- Q waves and infarction ----
    {
        key: "qWaves.pathologicalQWaves",
        section: "qWaves",
        leads: true,
        shortcuts: ["qpath"],
        text: {
            en: "Pathological Q waves in %s",
            it: "Onde Q patologiche in %s.",
            uk: "Патологічні зубці Q у %s",
        },
    },
    {
        key: "qWaves.qsPattern",
        section: "qWaves",
        leads: true,
        shortcuts: ["qs"],
        text: {
            en: "QS pattern in %s",
            it: "Pattern QS in %s.",
            uk: "Комплекс QS у %s",
        },
    },
    {
        key: "qWaves.possiblePreviousInfarction",
        section: "qWaves",
        leads: false,
        shortcuts: ["oldmi", "mi"],
        text: {
            en: "Possible previous myocardial infarction",
            it: "Possibile pregresso infarto miocardico.",
            uk: "Можливий перенесений інфаркт міокарда",
        },
    },

    // ---- intraventricular conduction ----
    {
        key: "ivConduction.rsrPattern",
        section: "ivConduction",
        leads: true,
        shortcuts: ["rsr"],
        text: {
            en: "rSR' pattern in %s",
            it: "Pattern rSR' in %s.",
            uk: "Патерн rSR' у %s",
        },
    },
    {
        key: "ivConduction.nonSpecificDelay",
        section: "ivConduction",
        leads: false,
        shortcuts: ["ivcd"],
        text: {
            en: "Non-specific intraventricular conduction delay",
            it: "Ritardo aspecifico della conduzione intraventricolare.",
            uk: "Неспецифічне порушення внутрішньошлуночкової провідності",
        },
    },
    {
        key: "ivConduction.focalDelay",
        section: "ivConduction",
        leads: true,
        shortcuts: ["focal"],
        text: {
            en: "Focal intraventricular conduction delay in %s",
            it: "Ritardo focale della conduzione intraventricolare in %s.",
            uk: "Вогнищеве порушення внутрішньошлуночкової провідності у %s",
        },
    },
    {
        key: "ivConduction.incompleteLBBB",
        section: "ivConduction",
        leads: false,
        shortcuts: ["ilbbb"],
        text: {
            en: "Left incomplete bundle branch block",
            it: "Blocco di branca sinistra incompleto.",
            uk: "Неповна блокада лівої ніжки пучка Гіса",
        },
    },
    {
        key: "ivConduction.trifascicularBlock",
        section: "ivConduction",
        leads: false,
        shortcuts: ["trifasc"],
        text: {
            en: "Trifascicular block",
            it: "Blocco trifascicolare.",
            uk: "Трифасцикулярна блокада",
        },
    },
    {
        key: "ivConduction.preExcitation",
        section: "ivConduction",
        leads: false,
        shortcuts: ["wpw", "preexcitation"],
        text: {
            en: "Ventricular pre-excitation (WPW pattern)",
            it: "Pre-eccitazione ventricolare (pattern WPW).",
            uk: "Шлуночкове передзбудження (патерн WPW)",
        },
    },
    {
        key: "ivConduction.lowVoltageLimbLeads",
        section: "ivConduction",
        leads: false,
        shortcuts: ["lowvoltlimb"],
        text: {
            en: "Low QRS voltage in the limb leads",
            it: "Basso voltaggio del QRS nelle derivazioni periferiche.",
            uk: "Низький вольтаж QRS у відведеннях від кінцівок",
        },
    },
    {
        key: "ivConduction.lowVoltageGeneralised",
        section: "ivConduction",
        leads: false,
        shortcuts: ["lowvolt"],
        text: {
            en: "Generalised low QRS voltage",
            it: "Basso voltaggio del QRS generalizzato.",
            uk: "Генералізований низький вольтаж QRS",
        },
    },
    {
        key: "ivConduction.poorRWaveProgression",
        section: "ivConduction",
        leads: false,
        shortcuts: ["prwp"],
        text: {
            en: "Poor R wave progression",
            it: "Scarsa progressione dell'onda R.",
            uk: "Погана прогресія зубця R",
        },
    },
    {
        key: "ivConduction.electricalAlternans",
        section: "ivConduction",
        leads: false,
        shortcuts: ["altern"],
        text: {
            en: "Electrical alternans",
            it: "Alternanza elettrica.",
            uk: "Електрична альтернація",
        },
    },

    // ---- QRS axis ----
    {
        key: "axis.leftAxisDeviation",
        section: "axis",
        leads: false,
        shortcuts: ["lad"],
        text: {
            en: "Left axis deviation",
            it: "Deviazione assiale sinistra.",
            uk: "Відхилення електричної осі вліво",
        },
    },
    {
        key: "axis.rightAxisDeviation",
        section: "axis",
        leads: false,
        shortcuts: ["rad"],
        text: {
            en: "Right axis deviation",
            it: "Deviazione assiale destra.",
            uk: "Відхилення електричної осі вправо",
        },
    },
    {
        key: "axis.extremeAxisDeviation",
        section: "axis",
        leads: false,
        shortcuts: ["ead"],
        text: {
            en: "Extreme axis deviation",
            it: "Deviazione assiale estrema.",
            uk: "Різке відхилення електричної осі",
        },
    },

    // ---- repolarisation ----
    {
        key: "repolarization.nonSpecificStTChanges",
        section: "repolarization",
        leads: false,
        shortcuts: ["nsstt"],
        text: {
            en: "Non-specific ST-T changes",
            it: "Alterazioni aspecifiche del tratto ST-T.",
            uk: "Неспецифічні зміни ST-T",
        },
    },
    {
        key: "repolarization.flatTWave",
        section: "repolarization",
        leads: true,
        shortcuts: ["flatt"],
        text: {
            en: "T wave flattening in %s",
            it: "Appiattimento dell'onda T in %s.",
            uk: "Сплощення зубця T у %s",
        },
    },
    {
        key: "repolarization.biphasicTWave",
        section: "repolarization",
        leads: true,
        shortcuts: ["biphasic"],
        text: {
            en: "Biphasic T wave in %s",
            it: "Onda T bifasica in %s.",
            uk: "Двофазний зубець T у %s",
        },
    },
    {
        key: "repolarization.tallPeakedTWaves",
        section: "repolarization",
        leads: true,
        shortcuts: ["tallt"],
        text: {
            en: "Tall peaked T waves in %s",
            it: "Onde T alte e appuntite in %s.",
            uk: "Високі загострені зубці T у %s",
        },
    },
    {
        key: "repolarization.prolongedQT",
        section: "repolarization",
        leads: false,
        shortcuts: ["lqt", "longqt"],
        text: {
            en: "Prolonged QT interval",
            it: "Intervallo QT prolungato.",
            uk: "Подовжений інтервал QT",
        },
    },
    {
        key: "repolarization.shortQT",
        section: "repolarization",
        leads: false,
        shortcuts: ["sqt"],
        text: {
            en: "Short QT interval",
            it: "Intervallo QT corto.",
            uk: "Укорочений інтервал QT",
        },
    },
    {
        key: "repolarization.earlyRepolarisation",
        section: "repolarization",
        leads: false,
        shortcuts: ["erp"],
        text: {
            en: "Early repolarisation pattern",
            it: "Pattern di ripolarizzazione precoce.",
            uk: "Патерн ранньої реполяризації",
        },
    },
    {
        key: "repolarization.jPointElevation",
        section: "repolarization",
        leads: true,
        shortcuts: ["jpoint"],
        text: {
            en: "J point elevation in %s",
            it: "Sopra-slivellamento del punto J in %s.",
            uk: "Елевація точки J у %s",
        },
    },
    {
        key: "repolarization.prominentUWaves",
        section: "repolarization",
        leads: false,
        shortcuts: ["uwave"],
        text: {
            en: "Prominent U waves",
            it: "Onde U prominenti.",
            uk: "Виражені зубці U",
        },
    },
    {
        key: "repolarization.diffuseStElevationWithPrDepression",
        section: "repolarization",
        leads: false,
        shortcuts: ["pericarditis"],
        text: {
            en: "Diffuse ST elevation with PR depression",
            it: "Sopra-slivellamento ST diffuso con sotto-slivellamento del PR.",
            uk: "Дифузна елевація ST з депресією PR",
        },
    },
    {
        key: "repolarization.digitalisEffect",
        section: "repolarization",
        leads: false,
        shortcuts: ["digitalis"],
        text: {
            en: "Repolarisation changes consistent with digitalis effect",
            it: "Alterazioni della ripolarizzazione compatibili con effetto digitalico.",
            uk: "Зміни реполяризації, характерні для дії дигіталісу",
        },
    },

    // ---- recording quality, not the patient ----
    {
        key: "technical.baselineWander",
        section: "technical",
        leads: false,
        shortcuts: ["baseline"],
        text: {
            en: "Baseline wander",
            it: "Deriva della linea di base.",
            uk: "Дрейф ізолінії",
        },
    },
    {
        key: "technical.muscleArtefact",
        section: "technical",
        leads: false,
        shortcuts: ["emg"],
        text: {
            en: "Muscle artefact",
            it: "Artefatto muscolare.",
            uk: "М'язовий артефакт",
        },
    },
    {
        key: "technical.mainsInterference",
        section: "technical",
        leads: false,
        shortcuts: ["mains"],
        text: {
            en: "Mains interference",
            it: "Interferenza da rete elettrica.",
            uk: "Мережева наводка",
        },
    },
    {
        key: "technical.limbLeadReversal",
        section: "technical",
        leads: false,
        shortcuts: ["leadreversal"],
        text: {
            en: "Limb lead reversal suspected",
            it: "Sospetta inversione delle derivazioni periferiche.",
            uk: "Підозра на переплутані відведення від кінцівок",
        },
    },
    {
        key: "technical.precordialMisplacement",
        section: "technical",
        leads: false,
        shortcuts: ["precordial"],
        text: {
            en: "Precordial lead misplacement suspected",
            it: "Sospetto posizionamento errato delle derivazioni precordiali.",
            uk: "Підозра на неправильне розташування грудних відведень",
        },
    },
    {
        key: "technical.dextrocardia",
        section: "technical",
        leads: false,
        shortcuts: ["dextro"],
        text: {
            en: "Dextrocardia or limb lead reversal",
            it: "Destrocardia o inversione delle derivazioni periferiche.",
            uk: "Декстрокардія або переплутані відведення від кінцівок",
        },
    },
];
